/**
 * /store — Donator upgrade page.
 *
 * One product card: $5 one-time, lists the perks, redirects to Stripe
 * Checkout when the user clicks. If the user is already a donator we
 * show the thank-you state instead.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Store() {
  const { user, refreshMe } = useAuth();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/store/donator/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const isDonator = status?.is_donator ?? user?.is_donator;
  const since = status?.donator_since ?? user?.donator_since;

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/store/checkout/donator", {
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("no checkout url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="sticky top-0 z-30 bg-black/60 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
          <Link to="/dashboard" data-testid="store-back" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 text-sm">
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 flex-1 text-center">Store</div>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 py-12">
        <div
          data-testid="store-donator-card"
          className="relative rounded-2xl border border-emerald-400/40 bg-gradient-to-br from-emerald-950 via-black to-black p-8 overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/15 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] uppercase tracking-[0.4em] mb-5">
              <Heart size={12} fill="currentColor" /> Donator
            </div>
            <h1 className="text-4xl font-black">$5</h1>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-1">One-time · lifetime status</p>

            <ul className="mt-6 space-y-3 text-sm text-zinc-300">
              <li className="flex items-start gap-2">
                <Sparkles size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                Your username glows{" "}
                <span style={{ color: "#39FF14", textShadow: "0 0 8px rgba(57,255,20,0.6)", fontWeight: 700 }}>neon green</span>{" "}
                everywhere in the app
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                Animated 💚 Donator badge on profile, lobbies & match screens
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                Show your support for FootGuesser
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                One-time payment, lifetime status — no subscription
              </li>
            </ul>

            <div className="mt-8">
              {isDonator ? (
                <div data-testid="store-already-donator" className="text-center">
                  <div className="text-emerald-300 font-bold text-lg">✅ You&apos;re already a Donator — thank you!</div>
                  {since && (
                    <div className="text-xs text-zinc-500 mt-2">
                      Donor since: {new Date(since).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="store-buy-donator"
                  onClick={buy}
                  disabled={busy}
                  className="w-full py-3 rounded-full bg-[var(--accent)] text-black font-black hover:brightness-110 disabled:opacity-40 transition"
                >
                  {busy ? "Redirecting to Stripe…" : "Become a Donator — $5"}
                </button>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-zinc-500 mt-6">Payment processed securely by Stripe.</p>
      </main>
    </div>
  );
}
